'use client';

import { useEffect, useState } from 'react';
import { Logo } from '@/components/ui/logo';
import { openCrispChat } from '@/shared/lib/crisp';
import { SignalLostGlobe } from './SignalLostGlobe';

export const RETRY_EVERY_SEC = 30;

export interface SignalLostScreenProps {
  /** Called when the countdown reaches zero (typically the boundary's `reset`). Retry is automatic — no manual button. */
  onRetry: () => void;
  retryEverySec?: number;
}

/**
 * Full-viewport "servers unreachable" screen. Visual port of taboo-globe's
 * `ErrorBoundary` fallback — same wrapper, grain, static, glow, motion — with
 * the meaning flipped: the globe is intact and the *link* is severed, amber
 * instead of red, because nothing is broken on the user's side.
 *
 * Presentation-only: no stores, no queries, no navigation. The boundary that
 * renders it owns retry semantics.
 */
export function SignalLostScreen({
  onRetry,
  retryEverySec = RETRY_EVERY_SEC,
}: SignalLostScreenProps) {
  const [secondsLeft, setSecondsLeft] = useState(retryEverySec);

  useEffect(() => {
    let left = retryEverySec;
    setSecondsLeft(left);
    const id = setInterval(() => {
      left -= 1;
      if (left <= 0) {
        left = retryEverySec;
        onRetry();
      }
      setSecondsLeft(left);
    }, 1000);
    return () => clearInterval(id);
  }, [retryEverySec, onRetry]);

  return (
    <div
      role="alert"
      aria-live="polite"
      className="sl-root fixed inset-0 z-[9999] flex flex-col overflow-hidden bg-[#0a0a0a] font-sans"
    >
      <div aria-hidden className="sl-grain" />
      <div aria-hidden className="sl-static" />

      {/* Same header slot as AuthLayout / TopHeader so the logo doesn't jump when the screen swaps in. */}
      <header className="relative z-[3] w-full border-b border-white/5 py-6">
        <div className="account-container">
          <div className="sl-logo inline-block text-white/70">
            <Logo size="md" linkTo="/" />
          </div>
        </div>
      </header>

      <main className="relative z-[3] flex flex-1 items-center justify-center px-4 py-8">
        <div className="grid w-full max-w-5xl grid-cols-1 items-center justify-items-center gap-6 md:grid-cols-2 md:gap-12">
          <div className="relative aspect-square w-[min(300px,52vw)] md:w-[min(420px,36vw)]">
            <div aria-hidden className="sl-glow" />
            <SignalLostGlobe />
          </div>

          <div className="sl-content flex max-w-[min(440px,86vw)] flex-col items-center gap-3 text-center md:items-start md:text-left">
            <h1 className="m-0 text-balance text-[clamp(22px,4vw,36px)] font-semibold leading-tight tracking-[-0.5px] text-white">
              Houston, we lost the signal.
            </h1>
            <p className="m-0 max-w-[380px] text-[clamp(13px,2vw,15px)] font-light leading-relaxed text-white/45">
              Our servers aren&apos;t answering. Your account and subscription are safe — we&apos;ll
              reconnect the moment they&apos;re back.
            </p>

            <div
              className="mt-1 flex items-center gap-2.5 text-xs uppercase tracking-[0.5px] text-white/40 tabular-nums"
              aria-live="off"
            >
              <span aria-hidden className="sl-beacon" />
              Reconnecting · next try in {secondsLeft}s
            </div>

            <button
              type="button"
              onClick={() => openCrispChat('app_error')}
              className="mt-3 rounded-lg border border-white/12 bg-transparent px-6 py-3 text-sm tracking-[0.3px] text-white/50 transition-all duration-200 hover:border-white/30 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Talk with us
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
